import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"

import { AppShell } from "@/components/app-shell.js"
import { NotFound } from "@/components/not-found.js"
import { supabase } from "@/lib/supabase.js"

/**
 * The guarded layout (block 02).
 *
 * A pathless route: it contributes no URL segment, so `_authed/index.tsx` is
 * `/` and every future protected screen is a file dropped in `_authed/`. That
 * is the OCP claim in this block's spec made structural — a new protected route
 * never edits the guard, it just lands inside it.
 *
 * The guard asks the narrow question ("is there a session?") and the shell asks
 * the wide one ("who is this?", via `useMe`). Deliberately not the same
 * question: `beforeLoad` cannot call a hook, and gating navigation on a network
 * round-trip to `/v1/me` would make every link wait on the API. `getSession()`
 * is a `localStorage` read plus, at most, a token refresh the SDK was going to
 * do anyway.
 *
 * A token the server no longer honours gets past this and 401s inside the shell.
 * `onUnauthorized` in `main.tsx` takes it from there: a deleted account goes to
 * `/signin`'s message, and a session Supabase confirms is dead is dropped, which
 * re-runs this guard and redirects.
 */
export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ location }) => {
    const { data } = await supabase.auth.getSession()

    // Sign-in returns here, so a bot link opened signed out lands on its nook (A5 C10).
    // `/` is where sign-in lands anyway, so it gets no `?redirect=`.
    if (!data.session) {
      const search = location.href === "/" ? {} : { redirect: location.href }
      throw redirect({ to: "/signin", search })
    }
  },
  // Here as well as the router default, so a stray URL keeps the sidebar.
  notFoundComponent: NotFound,
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
})
